import { useCallback, useEffect, useState } from 'react';
import type { SignalGateway, SignalTask } from './types';

interface SignalTasksState {
  readonly tasks: readonly SignalTask[];
  readonly loading: boolean;
  readonly completing: string | null;
  readonly complete: (taskId: string) => Promise<void>;
}

/**
 * Loads the FORQ Signal task list for the active tenant and keeps it in sync with the shell.
 * The same list feeds the topbar bell badge (count) and the open task center, so completing a
 * task updates both at once.
 */
export function useSignalTasks(
  gateway: SignalGateway,
  tenantId: string,
  permissions: ReadonlySet<string>,
): SignalTasksState {
  const [tasks, setTasks] = useState<readonly SignalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    gateway
      .listOpenTasks({ tenantId, permissions })
      .then((result) => {
        if (live) {
          setTasks(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (live) {
          setTasks([]);
          setLoading(false);
        }
      });
    return () => {
      live = false;
    };
  }, [gateway, tenantId, permissions]);

  const complete = useCallback(
    async (taskId: string) => {
      setCompleting(taskId);
      try {
        const result = await gateway.completeTask({ tenantId, taskId, permissions });
        setTasks(result);
      } finally {
        setCompleting(null);
      }
    },
    [gateway, tenantId, permissions],
  );

  return { tasks, loading, completing, complete };
}
